import { faEye, faPen, faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { RISelfOccupiedPropertiesDetailsForm } from ".";
import { RegimeTypes } from "../../../config/filingConstants";
import { IFormITR1, ISelfOccupiedProperties } from "../../../config/interfaces";
import { useAppSelector } from "../../../redux/Store";
import { ITRFilingSelector } from "../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const RISelfOccupiedPropertiesDetails: React.FC = () => {
  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<ISelfOccupiedProperties | null>(null);
  const [activeItem, setActiveItem] = useState<ISelfOccupiedProperties | null>(null);
  // used in both edit and delete cases
  const [activeItemIdx, setActiveItemIdx] = useState<string>("");

  const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();

  const RegimeType = values?.FilingReturnInfo.RegimeType || RegimeTypes.NEW_TAX_REGIME;
  const OccupiedPropertiesDetails = cloneDeep(values.IncomeDetails?.RISelfOccupiedProperties || []);

  const handleDeleteItem = async () => {
    OccupiedPropertiesDetails.splice(Number(activeItemIdx), 1);
    setFieldValue("IncomeDetails.RISelfOccupiedProperties", OccupiedPropertiesDetails);
    setShowDeleteModal(() => null);
    setActiveItem(() => null);
    setActiveItemIdx(() => "");
    handleTotalCalculation();
  };

  const handleTotalCalculation = () => {
    const SelfOccupied =
      OccupiedPropertiesDetails?.reduce((acc, curr) => {
        const value = parseInt(curr.NetTaxableIncome);
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    const _HousePropertyIncomes = {
      ...values.HousePropertyIncomes,
      TotalSelfOccupied: SelfOccupied
    };

    let _TotalHousePropertyIncome = 0;
    const _OnRent = values.HousePropertyIncomes.TotalOnRent;
    if (RegimeType === RegimeTypes.OLD_TAX_REGIME) {
      _TotalHousePropertyIncome += SelfOccupied + _OnRent;
    } else if (RegimeType === RegimeTypes.NEW_TAX_REGIME && _OnRent > 0) {
      _TotalHousePropertyIncome += _OnRent;
    }

    if (_TotalHousePropertyIncome < 0) {
      _TotalHousePropertyIncome = Math.max(_TotalHousePropertyIncome, -200000);
    }

    const DATA = {
      HousePropertyIncomes: _HousePropertyIncomes,
      TotalHousePropertyIncome: _TotalHousePropertyIncome
    };

    setValues({ ...cloneDeep(values), ...DATA });
    setFieldValue("IncomeDetails.RISelfOccupiedProperties", OccupiedPropertiesDetails);
  };

  return (
    <>
      <section className="mb-5">
        <div className="section-title-wrap">
          <h5>
            Self-occupied Properties (not given on rent)
            <small>
              Add address, loan interest, owner details etc. for properties not given on rent
            </small>
          </h5>
          <PreventEditComponent>
            {OccupiedPropertiesDetails.length === 0 ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddFormCanvas(() => true)}
              >
                <FASvgIcon icon={faPlus} />
                Add
              </Button>
            ) : (
              <Button
                variant="warning"
                size="sm"
                onClick={() => {
                  setShowAddFormCanvas(() => true);
                  setActiveItem(() => OccupiedPropertiesDetails[0]);
                }}
              >
                <FASvgIcon icon={faPen} />
                Edit
              </Button>
            )}
          </PreventEditComponent>

          {isFilingReadOnly && (
            <Button
              variant="warning"
              size="sm"
              className="text-white"
              onClick={() => setShowAddFormCanvas(() => true)}
            >
              <FASvgIcon icon={faEye} />
              View
            </Button>
          )}
        </div>

        {OccupiedPropertiesDetails.length !== 0 ? (
          <dl className="eds-list eds-dl eds-inline-between">
            <div key={`any-asset-${0}`} className="eds-list--item">
              <dt>Self-occupied Property </dt>
              <dd>
                <span>
                  <PriceDisplay price={OccupiedPropertiesDetails[0]?.NetTaxableIncome || 0} />
                </span>
                <PreventEditComponent>
                  <Button
                    variant="transparent"
                    className="icon-only"
                    onClick={() => {
                      setShowDeleteModal(() => OccupiedPropertiesDetails[0]);
                      setActiveItemIdx(() => String(0));
                    }}
                  >
                    <FASvgIcon icon={faTrash} />
                  </Button>
                </PreventEditComponent>
              </dd>
            </div>
          </dl>
        ) : null}
      </section>

      <RISelfOccupiedPropertiesDetailsForm
        showCanvas={showAddFormCanvas}
        handleCloseBtnPress={(assetData) => {
          if (assetData) {
            if (activeItem) {
              OccupiedPropertiesDetails.splice(Number(activeItemIdx), 1, assetData);
            } else {
              OccupiedPropertiesDetails.push(assetData);
            }
            handleTotalCalculation();
          }
          setShowAddFormCanvas(() => false);
          setActiveItem(() => null);
          setActiveItemIdx(() => "");
        }}
        formValues={activeItem}
      />

      <ConfirmationModal
        showModal={!!showDeleteModal}
        iconColorClass="text-danger"
        title="Are you sure?"
        message={<p>You want to delete Self-occupied Properties (not given on rent)</p>}
        onYesBtnClick={() => handleDeleteItem()}
        onNoBtnClick={() => setShowDeleteModal(() => null)}
        icon={faTrashAlt}
      />
    </>
  );
};

export default RISelfOccupiedPropertiesDetails;
